import type { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimWorkOrderChangeObjectData } from 'gql/graphql';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimWorkOrderChangeObjectsGql } from '../workOrderChangeQueries';

export const useGetWorkOrderChangeObjects = ({
    workOrderChangeId,
    resetAfterUnmount = true,
    withEffect = true,
}: {
    workOrderChangeId: number;
    resetAfterUnmount?: boolean;
    withEffect?: boolean;
}): {
    workOrderChangeObjectsData: BimWorkOrderChangeObjectData[];
    getWorkOrderChangeObjectsStatus: RequestStatus;
    getWorkOrderChangeObjectError: any;
    refreshWorkOrderChangeObjects: () => void;
    resetWorkOrderChangeObjects: () => void;
} => {
    const dispatch = useDispatch();

    const variables = useMemo(
        () => ({
            workOrderChangeId,
        }),
        [workOrderChangeId],
    );

    const [
        workOrderChangeObjectsData,
        getWorkOrderChangeObjectsStatus,
        getWorkOrderChangeObjectError,
        getWorkOrderChangeObjectsRequest,
    ] = useRequest({
        requestId: 'bimWorkOrderChangeObjects',
        query: bimWorkOrderChangeObjectsGql,
        variables,
        withEffect: !!workOrderChangeId && withEffect,
    });

    const [reset] = useResetStoreData({
        requestId: 'bimWorkOrderChangeObjects',
    });

    const refreshWorkOrderChangeObjects = useCallback(() => {
        dispatch(getWorkOrderChangeObjectsRequest(variables));
    }, [dispatch, getWorkOrderChangeObjectsRequest, variables]);

    const resetWorkOrderChangeObjects = useCallback(() => {
        reset(undefined);
    }, [reset]);

    useEffect(
        () => () => {
            if (resetAfterUnmount) {
                resetWorkOrderChangeObjects();
            }
        },
        [resetAfterUnmount, resetWorkOrderChangeObjects],
    );

    return {
        workOrderChangeObjectsData: workOrderChangeObjectsData?.bimWorkOrderChangeObjects?.items || [],
        getWorkOrderChangeObjectsStatus,
        getWorkOrderChangeObjectError,
        refreshWorkOrderChangeObjects,
        resetWorkOrderChangeObjects,
    };
};
